"use client";

/**
 * The project-channel card's two INITIAL READS: does this deal have a Slack
 * channel, and does it already have a customer chat link.
 *
 * Split from `useDealProjectChannel` (which owns the four WRITES) at the
 * module-size cap; the seam is reads-vs-writes, and it is the seam the file's
 * own doc comment already described.
 *
 * The one rule this module exists to keep: a FAILED read is not an empty
 * result. Both of these used to swallow the failure and settle as "no channel /
 * no link", so the card offered to create a second channel for a deal that
 * already had one. Failures are recorded and retryable instead.
 */

import { useCallback, useEffect, useState } from "react";

import { readErrorMessage, readJson } from "./read-response";

const STATUS_READ_FAILED = "Could not check whether this deal has a project channel.";
const LINK_READ_FAILED = "Could not check for an existing customer chat link.";

export interface DealProjectChannelReads {
  /** True until BOTH initial reads have settled. */
  initializing: boolean;
  hasChannel: boolean;
  channelUrl: string | null;
  linkUrl: string | null;
  setLinkUrl: (url: string | null) => void;

  /**
   * The channel-status read FAILED, so `hasChannel` is unknown — not false.
   * The card must show this instead of the create form.
   */
  statusError: string | null;
  /** Same, for the existing-chat-link read. */
  linkError: string | null;
  retryStatus: () => void;
  retryLink: () => void;

  /** Re-read the channel status after a write (create). */
  reloadStatus: () => Promise<void>;
  /** Local reset after a confirmed unlink, so the create form returns. */
  clearChannel: () => void;
}

export function useDealProjectChannelReads(dealId: string): DealProjectChannelReads {
  const [hasChannel, setHasChannel] = useState(false);
  const [channelUrl, setChannelUrl] = useState<string | null>(null);
  const [linkUrl, setLinkUrl] = useState<string | null>(null);
  const [statusReady, setStatusReady] = useState(false);
  const [linkReady, setLinkReady] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    try {
      const res = await fetch(`/api/projects/${dealId}/channel`, { credentials: "same-origin" });
      const body = await readJson(res);
      if (!res.ok) {
        setStatusError(readErrorMessage(body, STATUS_READ_FAILED));
        return;
      }
      const parsed = body as { hasChannel?: boolean; channelUrl?: string | null };
      setHasChannel(Boolean(parsed?.hasChannel));
      setChannelUrl(parsed?.channelUrl ?? null);
      setStatusError(null);
    } catch {
      setStatusError(STATUS_READ_FAILED);
    } finally {
      setStatusReady(true);
    }
  }, [dealId]);

  const loadLink = useCallback(async () => {
    try {
      const res = await fetch(`/api/projects/${dealId}/chat-link`, { credentials: "same-origin" });
      const body = await readJson(res);
      if (!res.ok) {
        setLinkError(readErrorMessage(body, LINK_READ_FAILED));
        return;
      }
      const parsed = body as { exists?: boolean; url?: string | null };
      if (parsed?.exists && parsed.url) setLinkUrl(parsed.url);
      setLinkError(null);
    } catch {
      setLinkError(LINK_READ_FAILED);
    } finally {
      setLinkReady(true);
    }
  }, [dealId]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    void loadLink();
  }, [loadLink]);

  const retryStatus = useCallback(() => {
    setStatusError(null);
    setStatusReady(false);
    void loadStatus();
  }, [loadStatus]);

  const retryLink = useCallback(() => {
    setLinkError(null);
    setLinkReady(false);
    void loadLink();
  }, [loadLink]);

  const clearChannel = useCallback(() => {
    setHasChannel(false);
    setChannelUrl(null);
    setLinkUrl(null);
  }, []);

  return {
    initializing: !statusReady || !linkReady,
    hasChannel,
    channelUrl,
    linkUrl,
    setLinkUrl,
    statusError,
    linkError,
    retryStatus,
    retryLink,
    reloadStatus: loadStatus,
    clearChannel,
  };
}
